import React, { useCallback, useState, useEffect } from 'react';
import { Upload, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/utils';
import { validateFilesBatch, DEFAULT_VALIDATION_CONFIG } from '../../../utils/fileValidation';

interface FileUploadZoneProps {
  isDragOver: boolean;
  isValidating?: boolean;
  existingFiles?: File[];
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

interface DragValidationState {
  isValidating: boolean;
  hasValidFiles: boolean;
  hasInvalidFiles: boolean;
  validCount: number;
  invalidCount: number;
  duplicateCount: number;
  warnings: string[];
  errors: string[];
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  isDragOver,
  isValidating = false,
  existingFiles = [],
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  className
}) => {
  const [dragValidation, setDragValidation] = useState<DragValidationState | null>(null);
  const handleClick = useCallback(() => {
    const input = document.getElementById('file-input') as HTMLInputElement;
    input?.click();
  }, []);

  // Enhanced drag over handler with validation preview
  const handleDragOver = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    onDragOver(e);
    
    // Get dragged files for validation preview
    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];
    
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    
    if (files.length > 0) {
      setDragValidation({ isValidating: true, hasValidFiles: false, hasInvalidFiles: false, validCount: 0, invalidCount: 0, duplicateCount: 0, warnings: [], errors: [] });
      
      try {
        const validation = await validateFilesBatch(files, existingFiles, DEFAULT_VALIDATION_CONFIG);
        
        setDragValidation({
          isValidating: false,
          hasValidFiles: validation.validFiles.length > 0,
          hasInvalidFiles: validation.invalidFiles.length > 0,
          validCount: validation.validFiles.length,
          invalidCount: validation.invalidFiles.length,
          duplicateCount: validation.duplicateFiles.length,
          warnings: validation.batchErrors,
          errors: validation.invalidFiles.flatMap(f => f.errors).slice(0, 3) // Show max 3 errors
        });
      } catch (error) {
        setDragValidation({
          isValidating: false,
          hasValidFiles: false,
          hasInvalidFiles: true,
          validCount: 0,
          invalidCount: files.length,
          duplicateCount: 0,
          warnings: [],
          errors: ['Validation failed']
        });
      }
    }
  }, [onDragOver, existingFiles]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onDragLeave(e);
    setDragValidation(null);
  }, [onDragLeave]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragValidation(null);
    onDrop(e);
  }, [onDrop]);

  // Clear validation state when drag ends
  useEffect(() => {
    if (!isDragOver) {
      setDragValidation(null);
    }
  }, [isDragOver]);

  return (
    <div className={cn('relative', className)}>
      <input
        id="file-input"
        type="file"
        multiple
        accept="image/*,video/*,application/postscript"
        onChange={onFileInput}
        className="hidden"
      />
      
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={cn(
          'border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-all duration-300',
          'hover:border-blue-400 hover:bg-blue-50/50',
          isDragOver && !dragValidation
            ? 'border-blue-500 bg-blue-50 scale-[1.02]'
            : isDragOver && dragValidation?.hasValidFiles && !dragValidation?.hasInvalidFiles
            ? 'border-green-500 bg-green-50 scale-[1.02]'
            : isDragOver && dragValidation?.hasInvalidFiles
            ? 'border-red-500 bg-red-50 scale-[1.02]'
            : isDragOver && dragValidation?.isValidating
            ? 'border-yellow-500 bg-yellow-50 scale-[1.02]'
            : 'border-gray-300 bg-gray-50/50',
          isValidating && 'pointer-events-none opacity-75'
        )}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className={cn(
            'w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all duration-300',
            isDragOver && dragValidation?.hasValidFiles && !dragValidation?.hasInvalidFiles
              ? 'bg-green-500 text-white scale-110'
              : isDragOver && dragValidation?.hasInvalidFiles
              ? 'bg-red-500 text-white scale-110'
              : isDragOver && dragValidation?.isValidating
              ? 'bg-yellow-500 text-white scale-110'
              : isDragOver
              ? 'bg-blue-500 text-white scale-110'
              : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
          )}>
            {isValidating || (dragValidation?.isValidating) ? (
              <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin" />
            ) : dragValidation?.hasValidFiles && !dragValidation?.hasInvalidFiles ? (
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10" />
            ) : dragValidation?.hasInvalidFiles ? (
              <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10" />
            ) : (
              <Upload className="w-8 h-8 sm:w-10 sm:h-10" />
            )}
          </div>
          
          <h3 className="text-lg sm:text-xl font-semibold text-foreground">
            {isValidating ? 'Validating files...' : 
             dragValidation?.isValidating ? 'Checking files...' :
             dragValidation?.hasValidFiles && !dragValidation?.hasInvalidFiles ? `Ready to upload ${dragValidation.validCount} file${dragValidation.validCount !== 1 ? 's' : ''}` :
             dragValidation?.hasInvalidFiles ? `${dragValidation.invalidCount} invalid file${dragValidation.invalidCount !== 1 ? 's' : ''}` :
             isDragOver ? 'Drop files here' : 'Drop files here or click to browse'}
          </h3>
          
          {/* Drag validation feedback */}
          {dragValidation && !dragValidation.isValidating && (
            <div className="space-y-2">
              {dragValidation.validCount > 0 && (
                <p className="text-sm text-green-600 font-medium">
                  ✓ {dragValidation.validCount} valid file{dragValidation.validCount !== 1 ? 's' : ''}
                </p>
              )}
              {dragValidation.duplicateCount > 0 && (
                <p className="text-sm text-yellow-600 font-medium">
                  ⚠ {dragValidation.duplicateCount} duplicate file{dragValidation.duplicateCount !== 1 ? 's' : ''}
                </p>
              )}
              {dragValidation.errors.length > 0 && (
                <div className="text-sm text-red-600 space-y-1">
                  {dragValidation.errors.slice(0, 2).map((error, index) => (
                    <p key={index}>✗ {error}</p>
                  ))}
                  {dragValidation.errors.length > 2 && (
                    <p>... and {dragValidation.errors.length - 2} more error{dragValidation.errors.length - 2 !== 1 ? 's' : ''}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {!dragValidation && (
            <p className="text-xs sm:text-sm text-muted-foreground px-2">
              Supports: JPG, PNG, SVG
            </p>
          )}
        </div>
        
        {!dragValidation && (
          <div className="text-xs text-muted-foreground space-y-2 px-2">
            <p>Max image size: 10MB</p>
            <p className="font-medium">Max number of files per upload: 100 files</p>
            <div className="pt-2 border-t border-gray-200">
              <p className="text-[10px] sm:text-xs text-blue-600 flex items-start gap-1">
                <span className="text-blue-500 mt-0.5">🔒</span>
                <span>
                  <strong>Privacy:</strong> We are not storing your original files. Only small optimized versions are kept for AI recognition.
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;